import { useLocale as useLocaleR42 } from '@/lib/locale';
/* eslint-disable react/no-unknown-property */
// siteSections.jsx — Phase 113 Pro Coach Website section components.
// ---------------------------------------------------------------------------
// Every section is theme-aware via the `.csite` CSS-variable scope applied by
// CoachSite.jsx. Sections receive the shared `site` object:
//   { coach, lp, packages, programs, testimonials, faqs, theme, slug,
//     mode: { isPreview, isParentPreview, isEditor },
//     cta: { text, disabled, onApply, contactText, contactVisible, onContact },
//     nav: (pageId) => void }
// In editor mode each section is wrapped in <Editable> which paints a dashed
// outline + "Edit" pill and posts a `sp-edit` message to the parent editor.

import React from 'react';
import { CoachAvatar } from '@/components/CoachChip';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import CustomSectionRenderer from '@/components/landing/CustomSectionRenderer';
import { fullAddress } from '@/components/LocationCards';
import { fmtPrice } from '@/lib/api';
import { formatSlotSchedule, countSlotSessions } from '@/lib/packageSchedule';
import { SWIM_PHOTOS } from '@/lib/swimPhotoLibrary';
import { getCopy } from '@/lib/siteCopy';
import api from '@/lib/api';
import { TravelRequestDialog } from '@/components/TravelRequestDialog';
import { visibleTeamMembers } from '@/lib/coachSiteDefaults';
import {
    ArrowRight, Award, CalendarRange, CheckCircle2, Clock, HeartHandshake,
    MapPin, Navigation, Pencil, Phone, ShieldCheck, Smile, Sparkles, Star,
    Users, Waves, MessageSquare, GraduationCap, Trophy, Timer, Baby, Sun,
    Droplets, Medal, Heart, UserRound, EyeOff, Route, Loader2,
} from 'lucide-react';

// Icon vocabulary for coach-editable highlight cards (Phase 114). Keys are
// stored in `site_copy.highlights.cards[].icon`; unknown keys fall back to
// a rotating default so a card is never icon-less.
export const HIGHLIGHT_ICONS = {
    shield: ShieldCheck,
    users: Users,
    award: Award,
    waves: Waves,
    graduation: GraduationCap,
    smile: Smile,
    calendar: CalendarRange,
    heart: HeartHandshake,
    trophy: Trophy,
    timer: Timer,
    baby: Baby,
    sun: Sun,
    droplets: Droplets,
    medal: Medal,
    love: Heart,
    star: Star,
};
export const HIGHLIGHT_ICON_KEYS = Object.keys(HIGHLIGHT_ICONS);

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

export function Editable({ id, site, children }) {
    const { t: t42 } = useLocaleR42();
    if (!site?.mode?.isEditor) return children;
    return (
        <div
            className="csite-editable"
            data-testid={`site-edit-${id}`}
            onClickCapture={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                    window.parent?.postMessage({ type: 'sp-edit', sectionId: id }, window.location.origin);
                } catch { /* ignore */ }
            }}
        >
            <span className="csite-edit-pill"><Pencil className="h-3 w-3" /> {t42("Edit")}</span>
            {children}
        </div>
    );
}

const Container = ({ children, className = '' }) => (
    <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 ${className}`}>{children}</div>
);

const SectionShell = ({ children, className = '', style, testid }) => (
    <section className={`csite-section ${className}`} style={style} data-testid={testid}>
        {children}
    </section>
);

const Heading = ({ children, className = '' }) => (
    <h2 className={`csite-display text-3xl font-bold leading-tight sm:text-4xl ${className}`}>{children}</h2>
);

// Layout-aware alignment for section intros (Phase 114 Layouts).
const isCentered = (site) => site?.layout?.headingAlign === 'center';

// Section heading + optional subheading, both coach-editable via site_copy.
const SectionIntro = ({ site, section, className = '', center, testid }) => {
    const heading = getCopy(site, section, 'heading');
    const sub = getCopy(site, section, 'subheading');
    const centered = center ?? isCentered(site);
    return (
        <div className={`${centered ? 'mx-auto text-center' : ''} max-w-2xl ${className}`} data-testid={testid}>
            {heading && <Heading>{heading}</Heading>}
            {sub && <p className={`mt-3 text-base ${centered ? 'mx-auto' : ''}`} style={{ color: 'var(--t-muted)' }}>{sub}</p>}
        </div>
    );
};

// Empty sections vanish entirely on the public site, but inside the editor
// they render a dashed placeholder so coaches understand why a section is
// blank and what to do about it.
const EmptySection = ({ site, testid, bg = 'var(--t-bg)', children }) => {
    if (!site?.mode?.isEditor) return null;
    return (
        <section className="py-10" style={{ background: bg }} data-testid={testid}>
            <Container>
                <div className="rounded-xl border-2 border-dashed px-6 py-8 text-center text-sm" style={{ borderColor: 'var(--t-border)', color: 'var(--t-muted)' }}>
                    {children}
                </div>
            </Container>
        </section>
    );
};

const defaultHeroPhotoByTheme = {
    'harbor-calm': 'kids-coach-teaching',
    'sunlit-deck': 'hero-lesson-wide',
    'cypress-retreat': 'kids-float-lesson',
    'lane-lines': 'hero-underwater-race',
    'studio-neutral': 'hero-freestyle',
};

export function resolveHeroPhoto(site) {
    const { lp, coach, theme } = site;
    if (lp?.hero_photo_url) return lp.hero_photo_url;
    if (coach?.photo_url) return coach.photo_url;
    const photoId = defaultHeroPhotoByTheme[theme.id] || 'kids-coach-teaching';
    return SWIM_PHOTOS.find((p) => p.id === photoId)?.url || SWIM_PHOTOS[0].url;
}

function heroCopy(site) {
    const { lp, coach } = site;
    const headline = (lp?.hero_headline || '').trim()
        || 'Swim lessons that build confidence for life';
    const sub = (lp?.hero_subheadline || '').trim()
        || `Trusted, personal swim instruction with ${coach?.display_name || 'your coach'}${coach?.service_area ? ` in ${String(coach.service_area).trim()}` : ''}. Safety-first lessons for every age and ability.`;
    return { headline, sub };
}

function HeroCtas({ site, center = false }) {
    const { cta } = site;
    return (
        <div className={`mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center ${center ? 'sm:justify-center' : ''}`}>
            <Button
                onClick={() => cta.onApply()}
                disabled={cta.disabled}
                className="csite-btn-primary csite-focus h-12 w-full px-7 text-base sm:w-auto"
                data-testid="site-hero-primary-cta"
            >
                {cta.text} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            {cta.contactVisible && (
                <Button
                    onClick={() => cta.onContact()}
                    className="csite-btn-outline csite-focus h-12 w-full px-6 text-base sm:w-auto"
                    data-testid="site-hero-contact-cta"
                >
                    <Phone className="mr-1.5 h-4 w-4" /> {cta.contactText}
                </Button>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// HERO — five theme-specific layouts
// ---------------------------------------------------------------------------

export function HeroSection({ site }) {
    const layout = site.layout?.heroLayout || site.theme.heroLayout;
    if (layout === 'photo-card') return <HeroPhotoCard site={site} />;
    if (layout === 'centered') return <HeroCentered site={site} />;
    if (layout === 'bento') return <HeroBento site={site} />;
    if (layout === 'classic') return <HeroClassic site={site} />;
    return <HeroSplit site={site} />;
}

function TrustEyebrow({ site }) {
    const text = getCopy(site, 'hero', 'eyebrow');
    if (!text) return null;
    return (
        <div className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
            style={{ background: 'var(--t-accent-2)', color: 'var(--t-text)' }} data-testid="site-hero-eyebrow">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{text}</span>
        </div>
    );
}

function HeroSplit({ site }) {
    const { headline, sub } = heroCopy(site);
    const photo = resolveHeroPhoto(site);
    return (
        <section className="relative overflow-hidden" data-testid="site-hero" style={{ background: 'var(--t-bg)' }}>
            <div className="absolute inset-x-0 top-0 h-[18%]" style={{ background: 'var(--t-hero-wash)' }} />
            <Container className="relative grid items-center gap-8 py-12 sm:gap-10 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
                <div>
                    <TrustEyebrow site={site} />
                    <h1 className="csite-display mt-5 text-4xl font-bold leading-[1.08] sm:text-5xl lg:text-6xl">{headline}</h1>
                    <p className="mt-5 max-w-xl text-base sm:text-lg" style={{ color: 'var(--t-muted)' }}>{sub}</p>
                    <HeroCtas site={site} />
                </div>
                <div className="relative">
                    <div className="overflow-hidden rounded-3xl border shadow-[0_18px_45px_rgba(10,22,40,0.14)]" style={{ borderColor: 'var(--t-border)' }}>
                        <img src={photo} alt={`${site.coach?.display_name || 'Coach'} swim lessons`} className="aspect-[4/3] w-full object-cover" loading="eager" />
                    </div>
                    {site.coach?.pool_name && (
                        <div className="absolute bottom-4 left-4 flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-full bg-white/85 px-3 py-1.5 text-xs font-semibold backdrop-blur" style={{ color: 'var(--t-text)' }}>
                            <MapPin className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--t-primary)' }} /> <span className="truncate">{site.coach.pool_name}</span>
                        </div>
                    )}
                </div>
            </Container>
        </section>
    );
}

function HeroPhotoCard({ site }) {
    const { headline, sub } = heroCopy(site);
    const photo = resolveHeroPhoto(site);
    const alt = `${site.coach?.display_name || 'Coach'} swim lessons`;
    return (
        <section className="relative" data-testid="site-hero" style={{ background: 'var(--t-bg)' }}>
            {/* Mobile: photo on top, content card overlapping below so the
                photo is never hidden behind the copy. */}
            <div className="sm:hidden">
                <div className="relative aspect-[4/3] w-full overflow-hidden">
                    <img src={photo} alt={alt} className="h-full w-full object-cover" loading="eager" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 50%)' }} />
                </div>
                <Container className="relative -mt-10 pb-10">
                    <div className="rounded-2xl border bg-white/95 p-5 shadow-[0_18px_45px_rgba(10,22,40,0.14)] backdrop-blur-md" style={{ borderColor: 'var(--t-border)' }}>
                        <TrustEyebrow site={site} />
                        <h1 className="csite-display mt-4 text-3xl font-bold leading-[1.1]">{headline}</h1>
                        <p className="mt-3 text-sm" style={{ color: 'var(--t-muted)' }}>{sub}</p>
                        <HeroCtas site={site} />
                    </div>
                </Container>
            </div>
            {/* Tablet / desktop: full-bleed photo with a floating content card. */}
            <div className="relative hidden h-[560px] overflow-hidden sm:block lg:h-[620px]">
                <img src={photo} alt={alt} className="h-full w-full object-cover" loading="eager" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.05) 55%, rgba(0,0,0,0) 100%)' }} />
                <Container className="absolute inset-x-0 bottom-0 left-1/2 -translate-x-1/2 pb-12">
                    <div className="max-w-xl rounded-2xl border bg-white/85 p-8 shadow-[0_18px_45px_rgba(10,22,40,0.14)] backdrop-blur-md" style={{ borderColor: 'var(--t-border)' }}>
                        <TrustEyebrow site={site} />
                        <h1 className="csite-display mt-4 text-4xl font-bold leading-[1.1] lg:text-5xl">{headline}</h1>
                        <p className="mt-3 text-base" style={{ color: 'var(--t-muted)' }}>{sub}</p>
                        <HeroCtas site={site} />
                    </div>
                </Container>
            </div>
        </section>
    );
}

function HeroCentered({ site }) {
    const { headline, sub } = heroCopy(site);
    const photo = resolveHeroPhoto(site);
    return (
        <section className="sd-noise relative overflow-hidden" data-testid="site-hero" style={{ background: 'var(--t-bg)' }}>
            <div className="h-1 w-full" style={{ background: 'var(--t-primary)' }} />
            <Container className="py-12 text-center sm:py-20">
                <div className="mx-auto max-w-3xl">
                    <TrustEyebrow site={site} />
                    <h1 className="csite-display mt-5 text-4xl font-bold leading-[1.08] sm:text-5xl">{headline}</h1>
                    <p className="mx-auto mt-5 max-w-2xl text-base sm:text-lg" style={{ color: 'var(--t-muted)' }}>{sub}</p>
                    <HeroCtas site={site} center />
                </div>
                <div className="mx-auto mt-10 max-w-4xl overflow-hidden rounded-2xl border shadow-[0_18px_45px_rgba(10,22,40,0.10)] sm:mt-12" style={{ borderColor: 'var(--t-border)' }}>
                    <img src={photo} alt={`${site.coach?.display_name || 'Coach'} swim lessons`} className="aspect-[4/3] w-full object-cover sm:aspect-[21/9]" loading="eager" />
                </div>
            </Container>
        </section>
    );
}

function HeroBento({ site }) {
    const { headline, sub } = heroCopy(site);
    const photo = resolveHeroPhoto(site);
    const { coach } = site;
    const metrics = [
        coach?.years_experience > 0 && { k: `${coach.years_experience}+`, v: 'Years coaching' },
        (coach?.certifications || []).length > 0 && { k: `${coach.certifications.length}`, v: 'Certifications' },
        (coach?.locations || []).length > 0 && { k: `${coach.locations.length}`, v: coach.locations.length === 1 ? 'Pool location' : 'Pool locations' },
    ].filter(Boolean).slice(0, 2);
    while (metrics.length < 2) metrics.push({ k: '1:1', v: 'Personal attention' });
    return (
        <section className="relative overflow-hidden" data-testid="site-hero" style={{ background: 'var(--t-bg)' }}>
            <div className="absolute right-0 top-0 h-56 w-56 rounded-full opacity-60 blur-3xl" style={{ background: 'var(--t-hero-wash)' }} />
            <Container className="relative grid gap-8 py-12 sm:py-20 lg:grid-cols-[1.1fr_0.9fr]">
                <div>
                    <TrustEyebrow site={site} />
                    <h1 className="csite-display mt-5 text-4xl font-bold leading-[1.08] sm:text-5xl lg:text-6xl">{headline}</h1>
                    <p className="mt-5 max-w-xl text-base sm:text-lg" style={{ color: 'var(--t-muted)' }}>{sub}</p>
                    <HeroCtas site={site} />
                    <div className="mt-8 grid max-w-md grid-cols-2 gap-3 sm:gap-4">
                        {metrics.map((m) => (
                            <div key={m.v} className="csite-card p-4">
                                <div className="text-2xl font-bold" style={{ fontFamily: "'Roboto Mono', monospace", color: 'var(--t-primary)' }}>{m.k}</div>
                                <div className="mt-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--t-muted)' }}>{m.v}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="overflow-hidden rounded-2xl border shadow-[0_18px_45px_rgba(10,22,40,0.14)]" style={{ borderColor: 'var(--t-border)' }}>
                    <img src={photo} alt={`${site.coach?.display_name || 'Coach'} swim lessons`} className="aspect-[4/3] w-full object-cover lg:aspect-auto lg:h-full lg:min-h-[320px]" loading="eager" />
                </div>
            </Container>
        </section>
    );
}

function HeroClassic({ site }) {
    const { headline, sub } = heroCopy(site);
    const photo = resolveHeroPhoto(site);
    return (
        <section className="sd-noise relative" data-testid="site-hero" style={{ background: 'var(--t-bg)' }}>
            <Container className="py-12 text-center sm:py-24">
                <div className="mx-auto max-w-3xl">
                    <TrustEyebrow site={site} />
                    <h1 className="csite-display mt-6 text-4xl font-bold leading-[1.05] sm:text-6xl">{headline}</h1>
                    <p className="mx-auto mt-6 max-w-2xl text-base sm:text-lg" style={{ color: 'var(--t-muted)' }}>{sub}</p>
                    <HeroCtas site={site} center />
                </div>
            </Container>
            <Container className="pb-12 sm:pb-16">
                <div className="overflow-hidden rounded-3xl border" style={{ borderColor: 'var(--t-border)' }}>
                    <img src={photo} alt={`${site.coach?.display_name || 'Coach'} swim lessons`} className="aspect-[4/3] w-full object-cover sm:aspect-[21/9]" loading="eager" />
                </div>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// TRUST STRIP
// ---------------------------------------------------------------------------

export function TrustStrip({ site }) {
    const { coach } = site;
    const pills = [];
    if (coach?.years_experience > 0) pills.push({ icon: Award, text: `${coach.years_experience}+ years experience` });
    (coach?.certifications || []).slice(0, 3).forEach((c) => pills.push({ icon: GraduationCap, text: c }));
    if (coach?.google_business_url) pills.push({ icon: Star, text: 'Reviewed on Google' });
    const verified = getCopy(site, 'trust_strip', 'verified_label');
    if (verified) pills.push({ icon: ShieldCheck, text: verified });
    return (
        <section className="border-y py-6" style={{ borderColor: 'var(--t-border)', background: 'var(--t-surface)' }} data-testid="site-trust-strip">
            <Container>
                <div className="flex flex-wrap items-center justify-center gap-3">
                    {pills.map((p) => (
                        <span key={p.text} className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold sm:text-sm" style={{ borderColor: 'var(--t-border)', color: 'var(--t-text)' }}>
                            <p.icon className="h-4 w-4" style={{ color: 'var(--t-primary)' }} /> {p.text}
                        </span>
                    ))}
                </div>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// HIGHLIGHTS — "Why families choose {coach}" (Aqua-Tots style benefit cards)
// ---------------------------------------------------------------------------

export function defaultHighlightCards(site) {
    const { coach, packages } = site;
    const hasFlexible = (packages || []).some((p) => p.flexible_schedule);
    return [
        { icon: 'shield', title: 'Safety comes first', desc: 'Every lesson is built around water safety fundamentals, so your child becomes a safe, confident swimmer for life.' },
        { icon: 'users', title: 'Personal attention', desc: 'Small class sizes and private options mean your swimmer gets the individual coaching they deserve.' },
        coach?.years_experience > 0
            ? { icon: 'award', title: 'Proven experience', desc: `${coach.years_experience}+ years of teaching swimmers of all ages and abilities.` }
            : { icon: 'waves', title: 'All ages & abilities', desc: 'From first splashes to stroke refinement — programs that meet swimmers exactly where they are.' },
        (coach?.certifications || []).length > 0
            ? { icon: 'graduation', title: 'Certified instruction', desc: coach.certifications.slice(0, 3).join(' · ') }
            : { icon: 'smile', title: 'Kids love lessons here', desc: 'Fun, encouraging coaching that turns nervous beginners into happy swimmers.' },
        hasFlexible
            ? { icon: 'calendar', title: 'Flexible scheduling', desc: 'Lesson times that work around your family — schedule directly with your coach.' }
            : { icon: 'calendar', title: 'Easy online booking', desc: 'Apply, sign your waiver and book — all online in just a few minutes.' },
        { icon: 'heart', title: 'A coach who cares', desc: 'Progress updates and open communication, so you always know how your swimmer is doing.' },
    ];
}

export function HighlightsSection({ site }) {
    const custom = site.lp?.site_copy?.highlights?.cards;
    const cards = (Array.isArray(custom) && custom.length ? custom : defaultHighlightCards(site))
        .filter((c) => c && (c.title || c.desc));
    if (!cards.length) return null;
    return (
        <SectionShell testid="site-highlights" style={{ background: 'var(--t-bg)' }}>
            <Container>
                <SectionIntro site={site} section="highlights" />
                {/* Mobile: horizontal snap carousel (one card + a peek). Tablet+: grid. */}
                <div className="csite-scroll-x -mx-4 mt-8 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:mt-10 sm:grid sm:grid-cols-2 sm:gap-6 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-3" data-testid="site-highlights-cards">
                    {cards.map((c, i) => {
                        const Icon = HIGHLIGHT_ICONS[c.icon] || HIGHLIGHT_ICONS[HIGHLIGHT_ICON_KEYS[i % HIGHLIGHT_ICON_KEYS.length]];
                        return (
                            <Card key={`${c.title}-${i}`} className="csite-card csite-card-hover w-[82%] shrink-0 snap-start border-0 p-5 sm:w-auto sm:p-6" style={{ border: '1px solid var(--t-border)' }} data-testid={`site-highlight-card-${i}`}>
                                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: 'var(--t-accent-2)', color: 'var(--t-primary)' }}>
                                    <Icon className="h-5 w-5" />
                                </span>
                                {c.title && <h3 className="csite-display mt-4 text-lg font-bold">{c.title}</h3>}
                                {c.desc && <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--t-muted)' }}>{c.desc}</p>}
                            </Card>
                        );
                    })}
                </div>
            </Container>
        </SectionShell>
    );
}

// ---------------------------------------------------------------------------
// PACKAGE CARD (shared by teaser + full programs page)
// ---------------------------------------------------------------------------

function PackageCard({ p, site }) {
    const { t: t42 } = useLocaleR42();
    const { cta } = site;
    const slots = Array.isArray(p.class_slots) ? p.class_slots : [];
    const hasSchedule = slots.length > 0;
    return (
        <Card className="csite-card csite-card-hover relative flex flex-col border-0 p-6" style={{ border: '1px solid var(--t-border)' }} data-testid={`site-package-card-${p.id}`}>
            {p.is_popular && (
                <Badge className="absolute -top-2.5 right-5 border-0" style={{ background: 'var(--t-accent)', color: 'var(--t-on-accent)' }}>{t42("Most popular")}</Badge>
            )}
            <h3 className="csite-display text-lg font-bold">{p.name}</h3>
            <div className="mt-1 flex items-baseline gap-1">
                <span className="text-3xl font-bold">{fmtPrice(p.price_cents, p?.currency)}</span>
                {p.package_type === 'subscription' && <span className="text-xs" style={{ color: 'var(--t-muted)' }}>/ {p.billing_interval}</span>}
            </div>
            {p.package_type === 'subscription' && (
                <Badge className="mt-2 w-fit border-0" style={{ background: 'var(--t-accent-2)', color: 'var(--t-text)' }}>{t42("Membership · renews")} {p.billing_interval}{t42("ly")}</Badge>
            )}
            {p.description && <p className="mt-2 text-sm" style={{ color: 'var(--t-muted)' }}>{p.description}</p>}
            <ul className="mt-4 space-y-1.5 text-sm" style={{ color: 'var(--t-muted)' }}>
                {!hasSchedule && (
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: 'var(--t-primary)' }} /> {p.session_count} {t42("sessions ·")} {p.duration_minutes} {t42("min")}</li>
                )}
                {!hasSchedule && p.flexible_schedule && (
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: 'var(--t-primary)' }} /> {t42("Flexible — coach sets times with you")}</li>
                )}
                {(p.age_group || p.swim_level) && (
                    <li className="flex items-center gap-2"><Users className="h-4 w-4 shrink-0" style={{ color: 'var(--t-primary)' }} /> {[p.age_group, p.swim_level].filter(Boolean).join(' · ')}</li>
                )}
            </ul>
            {hasSchedule && (
                <div className="mt-4 rounded-lg border p-3 text-sm" style={{ borderColor: 'var(--t-border)', color: 'var(--t-muted)' }}>
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--t-muted)' }}>
                        {slots.length > 1 ? t42("Available class times") : t42("Class schedule")}
                    </div>
                    <ul className="space-y-1.5">
                        {slots.map((s, idx) => {
                            const summary = formatSlotSchedule(s, p);
                            const sessions = countSlotSessions(s, p);
                            const duration = Number(s.duration_minutes) || Number(p.duration_minutes) || 0;
                            return (
                                <li key={s.id || idx} className="flex items-start gap-2">
                                    <CalendarRange className="mt-0.5 h-4 w-4 shrink-0" style={{ color: 'var(--t-primary)' }} />
                                    <div className="min-w-0 flex-1">
                                        {s.label && <div className="font-semibold" style={{ color: 'var(--t-text)' }}>{s.label}</div>}
                                        <div className="font-medium" style={{ color: 'var(--t-text)' }}>{summary || '—'}</div>
                                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
                                            {sessions !== null && sessions > 0 && <span>{sessions} {sessions === 1 ? t42("lesson") : t42("lessons")}</span>}
                                            {sessions === null && <span>{t42("Ongoing")}</span>}
                                            {duration > 0 && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {duration} {t42("min")}</span>}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
            {/* Phase 116a — teaching coach (name + photo) on every public card.
                Uses the site theme tokens so it blends with all 5 themes. */}
            {p.teaching_coach && (
                <div className="mt-4 flex items-center gap-2.5" data-testid={`site-package-coach-${p.id}`}>
                    <CoachAvatar coach={p.teaching_coach} size="sm" ringColor="var(--t-primary)" />
                    <div className="min-w-0 text-sm leading-tight">
                        <span className="text-xs" style={{ color: 'var(--t-muted)' }}>{t42("Coach")} </span>
                        <span className="font-semibold" style={{ color: 'var(--t-text)' }} data-testid={`site-package-coach-name-${p.id}`}>{p.teaching_coach.display_name}</span>
                    </div>
                </div>
            )}
            <Button
                onClick={() => cta.onApply(p.id)}
                disabled={cta.disabled}
                className="csite-btn-primary csite-focus mt-5 h-11"
                data-testid={`site-package-cta-${p.id}`}
            >
                {cta.text}
            </Button>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// PROGRAMS TEASER (home) + full PACKAGES section (programs page)
// ---------------------------------------------------------------------------

export function ProgramsTeaser({ site }) {
    const { t: t42 } = useLocaleR42();
    const { packages, lp, nav } = site;
    if (lp?.show_packages === false) return <HiddenPricingBlock site={site} compact />;
    const featured = (packages || []).slice(0, 3);
    if (!featured.length) {
        return <EmptySection site={site} testid="site-programs-teaser" bg="var(--t-surface)">{t42("No packages published yet — create packages in Classes & Packages to fill this section.")}</EmptySection>;
    }
    return (
        <SectionShell testid="site-programs-teaser" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <div className={`flex flex-wrap items-end justify-between gap-4 ${isCentered(site) ? 'flex-col items-center justify-center' : ''}`}>
                    <SectionIntro site={site} section="programs_teaser" />
                    <Button onClick={() => nav('programs')} className="csite-btn-outline csite-focus h-11 w-full px-5 sm:w-auto" data-testid="site-view-all-programs">
                        {getCopy(site, 'programs_teaser', 'button_label')} <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                </div>
                <div className="mt-8 grid gap-5 sm:mt-10 md:grid-cols-2 lg:grid-cols-3">
                    {featured.map((p) => <PackageCard key={p.id} p={p} site={site} />)}
                </div>
            </Container>
        </SectionShell>
    );
}

export function PackagesSection({ site }) {
    const { packages, lp } = site;
    if (lp?.show_packages === false) return <HiddenPricingBlock site={site} />;
    return (
        <SectionShell testid="site-packages" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <SectionIntro site={site} section="packages" />
                {(packages || []).length === 0 ? (
                    <div className="mt-8">
                        <Card className="csite-card border-0 p-8 text-center" style={{ border: '1px solid var(--t-border)', color: 'var(--t-muted)' }}>
                            {getCopy(site, 'packages', 'empty_text')}
                        </Card>
                    </div>
                ) : (
                    <div className="mt-8 grid gap-5 sm:mt-10 md:grid-cols-2 lg:grid-cols-3">
                        {packages.map((p) => <PackageCard key={p.id} p={p} site={site} />)}
                    </div>
                )}
            </Container>
        </SectionShell>
    );
}

function HiddenPricingBlock({ site, compact = false }) {
    const { t: t42 } = useLocaleR42();
    const { lp, coach, cta } = site;
    const headline = lp?.hidden_packages_headline || `Custom packages, tailored for your swimmer`;
    const message = lp?.hidden_packages_message || "Every swimmer is different. Apply now and I'll personally review your goals and reach out within 24 hours with a recommended plan and pricing.";
    const photos = (lp?.hidden_packages_photos || []).slice(0, 4);
    return (
        <SectionShell testid="site-hidden-pricing" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <div className="grid items-center gap-8 md:grid-cols-2">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider" style={{ background: 'var(--t-accent)', color: 'var(--t-on-accent)' }}>
                            <Sparkles className="h-3.5 w-3.5" /> {t42("Apply for pricing")} </div>
                        <h2 className="csite-display mt-4 text-3xl font-bold leading-tight sm:text-4xl">{headline}</h2>
                        <p className="mt-4 text-base leading-relaxed" style={{ color: 'var(--t-muted)' }}>{message}</p>
                        <Button onClick={() => cta.onApply()} disabled={cta.disabled} className="csite-btn-primary csite-focus mt-6 h-12 px-6 text-base" data-testid="site-hidden-pricing-cta">
                            {cta.text} <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                    </div>
                    {photos.length > 0 ? (
                        <div className={`grid gap-3 ${photos.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                            {photos.map((url, i) => (
                                <div key={url} className={`overflow-hidden rounded-2xl border ${photos.length === 3 && i === 0 ? 'row-span-2 aspect-[3/4]' : 'aspect-square'}`} style={{ borderColor: 'var(--t-border)' }}>
                                    <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
                                </div>
                            ))}
                        </div>
                    ) : !compact && (
                        <div className="aspect-[4/3] overflow-hidden rounded-3xl border" style={{ borderColor: 'var(--t-border)' }}>
                            <img src={resolveHeroPhoto(site)} alt="" loading="lazy" className="h-full w-full object-cover" />
                        </div>
                    )}
                </div>
            </Container>
        </SectionShell>
    );
}

// ---------------------------------------------------------------------------
// TESTIMONIALS — mosaic (first quote featured large) + optional Google badge
// ---------------------------------------------------------------------------

export function TestimonialsSection({ site }) {
    const { t: t42 } = useLocaleR42();
    const { lp, testimonials, coach } = site;
    const source = lp?.reviews_source || 'manual';
    // Prefer the landing-page testimonials; fall back to the coach's
    // collected testimonial rows (normalized to the same shape).
    const manual = (lp?.testimonials?.length ? lp.testimonials : (testimonials || []).map((t) => ({
        quote: t.text, name: t.parent_name, location: t.student_name ? `Parent of ${t.student_name}` : '', rating: 5,
    }))).filter((t) => t?.quote);
    const showManual = source !== 'google' && manual.length > 0;
    const showGoogle = ['google', 'both'].includes(source) && coach?.google_business_url;
    if (!showManual && !showGoogle) {
        return <EmptySection site={site} testid="site-testimonials">{t42("Add testimonials (or connect Google reviews) to show social proof here.")}</EmptySection>;
    }
    const [featured, ...rest] = manual;
    return (
        <SectionShell testid="site-testimonials" style={{ background: 'var(--t-bg)' }}>
            <Container>
                <SectionIntro site={site} section="testimonials" />
                {showManual && (
                    <div className="mt-8 grid gap-5 sm:mt-10 lg:grid-cols-3">
                        <Card className="csite-card border-0 p-6 sm:p-8 lg:col-span-2" style={{ border: '1px solid var(--t-border)' }}>
                            <Stars rating={featured.rating || 5} />
                            <p className="csite-display mt-4 text-xl font-semibold leading-relaxed sm:text-2xl">“{featured.quote}”</p>
                            <div className="mt-5 text-sm font-bold">{featured.name}</div>
                            {featured.location && <div className="text-xs" style={{ color: 'var(--t-muted)' }}>{featured.location}</div>}
                        </Card>
                        <div className="grid gap-5">
                            {rest.slice(0, 2).map((t) => (
                                <Card key={`${t.name}|${(t.quote || '').slice(0, 24)}`} className="csite-card border-0 p-6" style={{ border: '1px solid var(--t-border)' }}>
                                    <Stars rating={t.rating || 5} />
                                    <p className="mt-3 text-sm leading-relaxed" style={{ color: 'var(--t-muted)' }}>“{t.quote}”</p>
                                    <div className="mt-3 text-sm font-bold">{t.name}</div>
                                    {t.location && <div className="text-xs" style={{ color: 'var(--t-muted)' }}>{t.location}</div>}
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
                {rest.length > 2 && showManual && (
                    <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {rest.slice(2, 5).map((t) => (
                            <Card key={`${t.name}|${(t.quote || '').slice(0, 24)}`} className="csite-card border-0 p-6" style={{ border: '1px solid var(--t-border)' }}>
                                <Stars rating={t.rating || 5} />
                                <p className="mt-3 text-sm leading-relaxed" style={{ color: 'var(--t-muted)' }}>“{t.quote}”</p>
                                <div className="mt-3 text-sm font-bold">{t.name}</div>
                            </Card>
                        ))}
                    </div>
                )}
                {showGoogle && <div className="mt-8"><GoogleBadge site={site} /></div>}
            </Container>
        </SectionShell>
    );
}

function Stars({ rating = 5 }) {
    return (
        <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className="h-4 w-4" style={rating >= n ? { fill: 'var(--t-accent)', color: 'var(--t-accent)' } : { color: 'var(--t-border)' }} />
            ))}
        </div>
    );
}

function GoogleBadge({ site }) {
    const { t: t42 } = useLocaleR42();
    const { coach } = site;
    return (
        <Card className="csite-card flex flex-col items-start gap-4 border-0 p-6 sm:flex-row sm:items-center sm:justify-between" style={{ border: '1px solid var(--t-border)' }} data-testid="site-google-badge">
            <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl border" style={{ borderColor: 'var(--t-border)', background: 'var(--t-surface)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-6 w-6" aria-label={t42("Google")}>
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                    </svg>
                </span>
                <div>
                    <div className="csite-display text-base font-bold">{coach?.display_name} {t42("on Google")}</div>
                    <div className="text-xs" style={{ color: 'var(--t-muted)' }}>{getCopy(site, 'testimonials', 'google_subtitle')}</div>
                </div>
            </div>
            <Button asChild className="csite-btn-accent csite-focus h-10 w-full px-4 text-sm sm:w-auto" data-testid="site-google-badge-cta">
                <a href={coach.google_business_url} target="_blank" rel="noopener noreferrer">{getCopy(site, 'testimonials', 'google_button')} <ArrowRight className="ml-1 h-4 w-4" /></a>
            </Button>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// LOCATIONS — teaser (home) + full grid (locations / contact pages)
// ---------------------------------------------------------------------------

function ThemedLocationCard({ loc }) {
    const { t: t42 } = useLocaleR42();
    const addr = fullAddress(loc);
    const gmaps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr || loc.name || '')}`;
    const amaps = `https://maps.apple.com/?q=${encodeURIComponent(loc.name || 'Location')}&address=${encodeURIComponent(addr || '')}`;
    return (
        <Card className="csite-card csite-card-hover border-0 p-6" style={{ border: '1px solid var(--t-border)' }} data-testid={`site-location-card-${loc.id}`}>
            <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: 'var(--t-accent-2)', color: 'var(--t-primary)' }}>
                    <MapPin className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="csite-display text-base font-bold">{loc.name}</div>
                    {addr && <div className="mt-0.5 text-sm" style={{ color: 'var(--t-muted)' }}>{addr}</div>}
                    {loc.notes && <div className="mt-1 text-xs" style={{ color: 'var(--t-muted)' }}>{loc.notes}</div>}
                </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                <Button asChild className="csite-btn-outline csite-focus h-9 px-3 text-xs" data-testid={`site-location-gmaps-${loc.id}`}>
                    <a href={gmaps} target="_blank" rel="noopener noreferrer"><Navigation className="mr-1 h-3.5 w-3.5" /> {t42("Google Maps")}</a>
                </Button>
                <Button asChild className="csite-btn-outline csite-focus h-9 px-3 text-xs" data-testid={`site-location-amaps-${loc.id}`}>
                    <a href={amaps} target="_blank" rel="noopener noreferrer"><Navigation className="mr-1 h-3.5 w-3.5" /> {t42("Apple Maps")}</a>
                </Button>
            </div>
        </Card>
    );
}

export function LocationsTeaser({ site }) {
    const { coach, nav } = site;
    const locs = (coach?.locations || []).filter((l) => l && (l.address || l.name));
    const heading = getCopy(site, 'locations_teaser', 'heading');
    if (!locs.length) {
        if (coach?.pool_name || coach?.service_area) {
            return (
                <SectionShell testid="site-locations-teaser" style={{ background: 'var(--t-surface)' }}>
                    <Container className="text-center">
                        <Heading>{heading}</Heading>
                        <p className="mx-auto mt-3 max-w-xl text-base" style={{ color: 'var(--t-muted)' }}>
                            {[coach.pool_name, coach.service_area].filter(Boolean).join(' · ')}
                        </p>
                    </Container>
                </SectionShell>
            );
        }
        return null;
    }
    return (
        <SectionShell testid="site-locations-teaser" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <Heading>{heading}</Heading>
                    {locs.length > 2 && (
                        <Button onClick={() => nav('locations')} className="csite-btn-outline csite-focus h-10 px-4 text-sm" data-testid="site-view-all-locations">
                            {getCopy(site, 'locations_teaser', 'button_label')} <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                    )}
                </div>
                <div className="mt-8 grid gap-5 md:grid-cols-2">
                    {locs.slice(0, 2).map((l) => <ThemedLocationCard key={l.id} loc={l} />)}
                </div>
            </Container>
        </SectionShell>
    );
}

export function LocationsGrid({ site }) {
    const { t: t42 } = useLocaleR42();
    const { coach } = site;
    const locs = (coach?.locations || []).filter((l) => l && (l.address || l.name));
    if (!locs.length) {
        return <EmptySection site={site} testid="site-locations-grid" bg="var(--t-surface)">{t42("Add locations from Locations in your portal to show them here with tap-for-directions.")}</EmptySection>;
    }
    return (
        <SectionShell testid="site-locations-grid" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <SectionIntro site={site} section="locations_grid" />
                <div className="mt-8 grid gap-5 md:grid-cols-2">
                    {locs.map((l) => <ThemedLocationCard key={l.id} loc={l} />)}
                </div>
            </Container>
        </SectionShell>
    );
}

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------

export function FAQSection({ site }) {
    const { t: t42 } = useLocaleR42();
    const faqs = site.faqs || [];
    if (!faqs.length) {
        return <EmptySection site={site} testid="site-faq">{t42("Add FAQs from your portal settings to answer common parent questions here.")}</EmptySection>;
    }
    return (
        <SectionShell testid="site-faq" style={{ background: 'var(--t-bg)' }}>
            <Container className="max-w-3xl">
                <Heading className={isCentered(site) ? 'text-center' : ''}>{getCopy(site, 'faq', 'heading')}</Heading>
                <Accordion type="single" collapsible className="mt-6">
                    {faqs.map((f) => (
                        <AccordionItem key={f.id} value={f.id} style={{ borderColor: 'var(--t-border)' }}>
                            <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{f.question}</AccordionTrigger>
                            <AccordionContent className="text-sm leading-relaxed" style={{ color: 'var(--t-muted)' }}>{f.answer}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </Container>
        </SectionShell>
    );
}

// ---------------------------------------------------------------------------
// CTA BAND — final conversion band (dark, themed)
// ---------------------------------------------------------------------------

export function CTABand({ site }) {
    const { t: t42 } = useLocaleR42();
    const { coach, cta } = site;
    const phone = (site.lp?.contact_phone || coach?.phone || '').replace(/[^\d+]/g, '');
    const heading = getCopy(site, 'cta_band', 'heading');
    const sub = getCopy(site, 'cta_band', 'subheading');
    return (
        <section className="py-14 sm:py-20" style={{ background: 'var(--t-dark-band)' }} data-testid="site-cta-band">
            <Container className="text-center">
                {heading && <h2 className="csite-display text-3xl font-bold leading-tight text-white sm:text-4xl">{heading}</h2>}
                {sub && <p className="mx-auto mt-3 max-w-xl text-base text-white/75">{sub}</p>}
                <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
                    <Button onClick={() => cta.onApply()} disabled={cta.disabled} className="csite-btn-accent csite-focus h-12 px-8 text-base" data-testid="site-cta-band-primary">
                        {cta.text} <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                    {phone && (
                        <Button asChild className="csite-focus h-12 border border-white/25 bg-white/10 px-6 text-base font-semibold text-white hover:bg-white/20" style={{ borderRadius: 'var(--t-radius-btn)' }} data-testid="site-cta-band-call">
                            <a href={`tel:${phone}`}><Phone className="mr-1.5 h-4 w-4" /> {t42("Call")} {site.lp?.contact_phone || coach?.phone}</a>
                        </Button>
                    )}
                </div>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// PAGE HERO (About / Programs / Team / Locations / Contact intros)
// ---------------------------------------------------------------------------

export function PageHero({ site, page }) {
    const section = `${page}_hero`;
    const title = getCopy(site, section, 'title');
    const sub = getCopy(site, section, 'subtitle');
    return (
        <section className="relative overflow-hidden border-b" style={{ background: 'var(--t-bg)', borderColor: 'var(--t-border)' }} data-testid={`site-page-hero-${page}`}>
            <div className="absolute inset-x-0 top-0 h-full opacity-70" style={{ background: 'var(--t-hero-wash)' }} />
            <Container className={`relative py-12 sm:py-16 ${isCentered(site) ? 'text-center' : ''}`}>
                <h1 className="csite-display text-4xl font-bold leading-tight sm:text-5xl">{title}</h1>
                {sub && <p className={`mt-3 max-w-xl text-base sm:text-lg ${isCentered(site) ? 'mx-auto' : ''}`} style={{ color: 'var(--t-muted)' }}>{sub}</p>}
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// ABOUT PAGE SECTIONS
// ---------------------------------------------------------------------------

export function StorySection({ site }) {
    const { t: t42 } = useLocaleR42();
    const { lp, coach } = site;
    const text = (lp?.about_html || '').trim() || (coach?.bio || '').trim();
    if (!text) {
        return <EmptySection site={site} testid="site-story" bg="var(--t-surface)">{t42("Write your story in the About section to introduce yourself to families.")}</EmptySection>;
    }
    return (
        <SectionShell testid="site-story" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <div className="grid items-start gap-8 sm:gap-10 lg:grid-cols-[0.9fr_1.1fr]">
                    {coach?.photo_url ? (
                        <div className="mx-auto w-full max-w-sm overflow-hidden rounded-3xl border shadow-[0_18px_45px_rgba(10,22,40,0.10)] lg:max-w-none" style={{ borderColor: 'var(--t-border)' }}>
                            <img src={coach.photo_url} alt={coach.display_name} className="aspect-[4/5] w-full object-cover" loading="lazy" />
                        </div>
                    ) : (
                        <div className="mx-auto flex aspect-[4/5] w-full max-w-sm items-center justify-center rounded-3xl border text-6xl font-bold lg:max-w-none" style={{ borderColor: 'var(--t-border)', background: 'var(--t-accent-2)', color: 'var(--t-primary)' }}>
                            {(coach?.display_name || 'SC').slice(0, 2).toUpperCase()}
                        </div>
                    )}
                    <div>
                        <Heading>{getCopy(site, 'story', 'heading')}</Heading>
                        <div className="mt-5 whitespace-pre-line text-base leading-relaxed" style={{ color: 'var(--t-muted)' }}>{text}</div>
                    </div>
                </div>
            </Container>
        </SectionShell>
    );
}

export function CredentialsSection({ site }) {
    const { t: t42 } = useLocaleR42();
    const { coach } = site;
    const certs = coach?.certifications || [];
    if (!certs.length && !(coach?.years_experience > 0)) {
        return <EmptySection site={site} testid="site-credentials">{t42("Add certifications and experience in your Profile to build trust here.")}</EmptySection>;
    }
    const yearsLabel = getCopy(site, 'credentials', 'years_label').replace('{n}', String(coach?.years_experience || 0));
    return (
        <SectionShell testid="site-credentials" style={{ background: 'var(--t-bg)' }}>
            <Container>
                <Heading className={isCentered(site) ? 'text-center' : ''}>{getCopy(site, 'credentials', 'heading')}</Heading>
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {coach?.years_experience > 0 && (
                        <Card className="csite-card border-0 p-6" style={{ border: '1px solid var(--t-border)' }}>
                            <Award className="h-6 w-6" style={{ color: 'var(--t-primary)' }} />
                            <div className="csite-display mt-3 text-lg font-bold">{yearsLabel}</div>
                        </Card>
                    )}
                    {certs.map((c) => (
                        <Card key={c} className="csite-card border-0 p-6" style={{ border: '1px solid var(--t-border)' }}>
                            <GraduationCap className="h-6 w-6" style={{ color: 'var(--t-primary)' }} />
                            <div className="csite-display mt-3 text-lg font-bold">{c}</div>
                        </Card>
                    ))}
                </div>
            </Container>
        </SectionShell>
    );
}

export function GallerySection({ site }) {
    const { t: t42 } = useLocaleR42();
    const photos = site.lp?.gallery_photos || [];
    if (!photos.length) {
        return <EmptySection site={site} testid="site-gallery" bg="var(--t-surface)">{t42("Add up to 6 photos in the Photo Gallery section — or pick from the curated swim library.")}</EmptySection>;
    }
    return (
        <SectionShell testid="site-gallery" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <SectionIntro site={site} section="gallery" />
                <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-3">
                    {photos.map((url, i) => (
                        <div key={url} className={`overflow-hidden rounded-2xl border ${i === 0 ? 'col-span-2 row-span-2 lg:col-span-1' : ''}`} style={{ borderColor: 'var(--t-border)' }}>
                            <img src={url} alt={`Lesson photo ${i + 1}`} loading="lazy" className="aspect-square h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                        </div>
                    ))}
                </div>
            </Container>
        </SectionShell>
    );
}

function embedUrl(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        if (u.hostname.includes('youtube.com')) {
            const id = u.searchParams.get('v');
            return id ? `https://www.youtube.com/embed/${id}` : null;
        }
        if (u.hostname === 'youtu.be') {
            const id = u.pathname.replace(/^\//, '');
            return id ? `https://www.youtube.com/embed/${id}` : null;
        }
        if (u.hostname.includes('vimeo.com')) {
            const id = u.pathname.split('/').filter(Boolean).pop();
            return id ? `https://player.vimeo.com/video/${id}` : null;
        }
    } catch { /* invalid url */ }
    return null;
}

export function VideosSection({ site }) {
    const { t: t42 } = useLocaleR42();
    const urls = (site.lp?.video_urls || []).map(embedUrl).filter(Boolean);
    if (!urls.length) {
        return <EmptySection site={site} testid="site-videos">{t42("Add YouTube or Vimeo links in the Videos section to show yourself in action.")}</EmptySection>;
    }
    return (
        <SectionShell testid="site-videos" style={{ background: 'var(--t-bg)' }}>
            <Container>
                <Heading className={isCentered(site) ? 'text-center' : ''}>{getCopy(site, 'videos', 'heading')}</Heading>
                <div className={`mt-8 grid gap-6 ${urls.length > 1 ? 'md:grid-cols-2' : 'max-w-3xl'}`}>
                    {urls.map((e, i) => (
                        <div key={e} className="aspect-video overflow-hidden rounded-2xl border bg-black" style={{ borderColor: 'var(--t-border)' }}>
                            <iframe src={e} title={`Video ${i + 1}`} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="h-full w-full" />
                        </div>
                    ))}
                </div>
            </Container>
        </SectionShell>
    );
}

// ---------------------------------------------------------------------------
// CONTACT PAGE
// ---------------------------------------------------------------------------

export function ContactCardSection({ site }) {
    const { t: t42 } = useLocaleR42();
    const { coach, lp, cta } = site;
    const phone = (lp?.contact_phone || coach?.phone || '').trim();
    const phoneClean = phone.replace(/[^\d+]/g, '');
    const c = (k) => getCopy(site, 'contact_card', k);
    return (
        <SectionShell testid="site-contact-card" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <div className="grid gap-5 md:grid-cols-3">
                    <Card className="csite-card border-0 p-6 text-center" style={{ border: '1px solid var(--t-border)' }}>
                        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: 'var(--t-accent-2)', color: 'var(--t-primary)' }}>
                            <Sparkles className="h-6 w-6" />
                        </span>
                        <h3 className="csite-display mt-4 text-lg font-bold">{c('apply_title')}</h3>
                        <p className="mt-2 text-sm" style={{ color: 'var(--t-muted)' }}>{c('apply_desc')}</p>
                        <Button onClick={() => cta.onApply()} disabled={cta.disabled} className="csite-btn-primary csite-focus mt-4 h-11 w-full" data-testid="site-contact-apply">
                            {cta.text}
                        </Button>
                    </Card>
                    <Card className="csite-card border-0 p-6 text-center" style={{ border: '1px solid var(--t-border)' }}>
                        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: 'var(--t-accent-2)', color: 'var(--t-primary)' }}>
                            <Phone className="h-6 w-6" />
                        </span>
                        <h3 className="csite-display mt-4 text-lg font-bold">{c('call_title')}</h3>
                        <p className="mt-2 text-sm" style={{ color: 'var(--t-muted)' }}>{phone || c('call_desc')}</p>
                        {phoneClean ? (
                            <Button asChild className="csite-btn-outline csite-focus mt-4 h-11 w-full" data-testid="site-contact-call">
                                <a href={`tel:${phoneClean}`}><Phone className="mr-1.5 h-4 w-4" /> {c('call_button')}</a>
                            </Button>
                        ) : (
                            <Button onClick={() => cta.onContact()} className="csite-btn-outline csite-focus mt-4 h-11 w-full" data-testid="site-contact-call-fallback"> {t42("Contact options")} </Button>
                        )}
                    </Card>
                    <Card className="csite-card border-0 p-6 text-center" style={{ border: '1px solid var(--t-border)' }}>
                        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: 'var(--t-accent-2)', color: 'var(--t-primary)' }}>
                            <MessageSquare className="h-6 w-6" />
                        </span>
                        <h3 className="csite-display mt-4 text-lg font-bold">{c('message_title')}</h3>
                        <p className="mt-2 text-sm" style={{ color: 'var(--t-muted)' }}>{c('message_desc')}</p>
                        <Button onClick={() => cta.onContact()} className="csite-btn-outline csite-focus mt-4 h-11 w-full" data-testid="site-contact-message">
                            {cta.contactText}
                        </Button>
                    </Card>
                </div>
            </Container>
        </SectionShell>
    );
}

// ---------------------------------------------------------------------------
// MEET THE TEAM (Phase 114) — members from landing_page.site_team
// ---------------------------------------------------------------------------

function TeamMemberCard({ m, hidden = false }) {
    const { t: t42 } = useLocaleR42();
    const initials = (m.name || '?').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
    const certs = (m.certifications || '').split(/[,·]/).map((s) => s.trim()).filter(Boolean);
    return (
        <Card className={`csite-card csite-card-hover relative flex flex-col overflow-hidden border-0 ${hidden ? 'opacity-50' : ''}`} style={{ border: '1px solid var(--t-border)' }} data-testid={`site-team-member-${m.id}`}>
            {hidden && (
                <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                    <EyeOff className="h-3 w-3" /> {t42("Hidden")} </span>
            )}
            {m.photo_url ? (
                <img src={m.photo_url} alt={m.name} loading="lazy" className="aspect-[4/5] w-full object-cover" />
            ) : (
                <div className="flex aspect-[4/3] w-full items-center justify-center" style={{ background: 'var(--t-accent-2)' }}>
                    {initials ? (
                        <span className="csite-display text-5xl font-bold" style={{ color: 'var(--t-primary)' }}>{initials}</span>
                    ) : (
                        <UserRound className="h-14 w-14" style={{ color: 'var(--t-primary)' }} />
                    )}
                </div>
            )}
            <div className="flex flex-1 flex-col p-5">
                <h3 className="csite-display text-lg font-bold leading-tight">{m.name}</h3>
                {m.role && <div className="mt-1 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--t-primary)' }}>{m.role}</div>}
                {m.bio && <p className="mt-3 text-sm leading-relaxed" style={{ color: 'var(--t-muted)' }}>{m.bio}</p>}
                {certs.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                        {certs.map((c) => (
                            <span key={c} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold" style={{ borderColor: 'var(--t-border)', color: 'var(--t-text)' }}>
                                <GraduationCap className="h-3 w-3" style={{ color: 'var(--t-primary)' }} /> {c}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
}

export function TeamGridSection({ site }) {
    const { t: t42 } = useLocaleR42();
    const { lp } = site;
    const isEditor = !!site?.mode?.isEditor;
    const all = (Array.isArray(lp?.site_team?.members) ? lp.site_team.members : []).filter((m) => m && (m.name || '').trim());
    const members = isEditor ? all : visibleTeamMembers(lp);
    if (!members.length) {
        return (
            <EmptySection site={site} testid="site-team-grid" bg="var(--t-surface)"> {t42("No team members yet — click this section to import your staff from Team Management or add coaches by hand.")} </EmptySection>
        );
    }
    const heading = (lp?.site_team?.heading || '').trim() || getCopy(site, 'team_grid', 'heading');
    const sub = (lp?.site_team?.subheading || '').trim() || getCopy(site, 'team_grid', 'subheading');
    const cols = members.length === 1 ? 'sm:grid-cols-1 sm:max-w-sm' : members.length === 2 ? 'sm:grid-cols-2 lg:max-w-3xl' : 'sm:grid-cols-2 lg:grid-cols-3';
    return (
        <SectionShell testid="site-team-grid" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <div className={`max-w-2xl ${isCentered(site) ? 'mx-auto text-center' : ''}`}>
                    {heading && <Heading>{heading}</Heading>}
                    {sub && <p className="mt-3 text-base" style={{ color: 'var(--t-muted)' }}>{sub}</p>}
                </div>
                <div className={`mx-auto mt-8 grid gap-5 sm:mt-10 ${cols}`} data-testid="site-team-members">
                    {members.map((m) => <TeamMemberCard key={m.id} m={m} hidden={isEditor && m.visible === false} />)}
                </div>
            </Container>
        </SectionShell>
    );
}

// ---------------------------------------------------------------------------
// SECTION SWITCH — id → component (custom sections delegate to the
// existing CustomSectionRenderer so nothing the coach built is lost)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// TRAVEL SMART — "Do we come to you?" (Phase 136, Pro)
// Renders only when the coach has Travel Smart live (coach.travel_smart.enabled).
// In the editor it shows a dashed hint when off so coaches know what it is.
// ---------------------------------------------------------------------------

export function TravelCheckSection({ site }) {
    const { t: t42 } = useLocaleR42();
    const { coach, slug } = site;
    const ts = coach?.travel_smart;
    const heading = getCopy(site, 'travel_check', 'heading');
    const sub = getCopy(site, 'travel_check', 'subheading');
    const buttonLabel = getCopy(site, 'travel_check', 'button_label');
    const [address, setAddress] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [result, setResult] = React.useState(null);
    const [requestOpen, setRequestOpen] = React.useState(false);

    if (!ts?.enabled) {
        return (
            <EmptySection site={site} testid="site-travel-check" bg="var(--t-surface)">
                <strong>{t42("We come to you")}</strong> {t42("— an address checker for in-home lessons. Turn on")} <strong>{t42("Travel Smart")}</strong> {t42("from your Locations page and this section appears here automatically.")} </EmptySection>
        );
    }

    const check = async () => {
        if (site.mode?.isEditor || site.mode?.isPreview) {
            setResult({ ok: true, geocoded: true, preview: true, in_radius: true, matched_count: 12, hidden_count: 3, distance_from_base_miles: 4.2, days: [] });
            return;
        }
        if (address.trim().length < 6) return;
        setBusy(true);
        setResult(null);
        try {
            const r = await api.post(`/public/coach/${slug}/travel-check`, { address: address.trim() });
            setResult(r.data);
        } catch (e) {
            setResult({ ok: false, message: e?.response?.data?.detail || 'Something went wrong — please try again.' });
        } finally {
            setBusy(false);
        }
    };

    const fmt = (t) => {
        const [h, m] = (t || '').split(':').map(Number);
        if (Number.isNaN(h)) return t;
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hh = ((h + 11) % 12) + 1;
        return `${hh}:${String(m || 0).padStart(2, '0')} ${ampm}`;
    };
    const dayLabel = (d) => {
        const [y, mo, da] = (d || '').split('-').map(Number);
        const dt = new Date(y, (mo || 1) - 1, da || 1);
        return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    };

    return (
        <SectionShell testid="site-travel-check" style={{ background: 'var(--t-surface)' }}>
            <Container>
                <div className="csite-card mx-auto max-w-3xl p-6 sm:p-8" data-testid="site-travel-check-card">
                    <div className="flex items-start gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: 'var(--t-primary)', color: 'var(--t-on-primary)' }}>
                            <Route className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                            <Heading className="text-2xl sm:text-3xl">{heading}</Heading>
                            {sub && <p className="mt-2 text-base" style={{ color: 'var(--t-muted)' }}>{sub}</p>}
                        </div>
                    </div>
                    <form
                        className="mt-6 flex flex-col gap-2 sm:flex-row"
                        onSubmit={(e) => { e.preventDefault(); check(); }}
                        data-testid="site-travel-check-form"
                    >
                        <input
                            type="text"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder={t42("123 Main St, City, ST 12345")}
                            aria-label={t42("Your address")}
                            className="csite-focus h-12 flex-1 px-4 text-base outline-none"
                            style={{ background: 'var(--t-bg)', border: '1px solid var(--t-border)', borderRadius: 'var(--t-radius-btn)', color: 'var(--t-text)' }}
                            data-testid="site-travel-check-input"
                        />
                        <Button type="submit" disabled={busy || address.trim().length < 6} className="csite-btn-primary csite-focus h-12 px-6 text-base" data-testid="site-travel-check-submit">
                            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MapPin className="mr-1.5 h-4 w-4" />}
                            {buttonLabel}
                        </Button>
                    </form>

                    {result && (
                        <div className="mt-5 rounded-xl p-4" style={{ background: 'var(--t-bg)', border: '1px solid var(--t-border)' }} data-testid="site-travel-check-result">
                            {!result.ok ? (
                                <p className="text-sm" style={{ color: 'var(--t-text)' }} data-testid="site-travel-check-nomatch">{result.message}</p>
                            ) : result.preview ? (
                                <p className="text-sm" style={{ color: 'var(--t-muted)' }}>{t42("Preview — on your live site parents see whether their address is on your route and the times that fit.")}</p>
                            ) : result.in_radius && result.matched_count > 0 ? (
                                <>
                                    <div className="flex items-start gap-2">
                                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: 'var(--t-primary)' }} />
                                        <div>
                                            <div className="font-semibold" style={{ color: 'var(--t-text)' }} data-testid="site-travel-check-yes"> {t42("Yes — we come to your area!")} </div>
                                            <p className="mt-0.5 text-sm" style={{ color: 'var(--t-muted)' }}>
                                                {result.matched_count} {t42("open")} {result.matched_count === 1 ? t42("time fits") : t42("times fit")} {t42("our route in the next two weeks")} {result.hidden_count > 0 ? ` (${result.hidden_count} more are on days we're across town)` : ''}.
                                            </p>
                                        </div>
                                    </div>
                                    {Array.isArray(result.days) && result.days.length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-2" data-testid="site-travel-check-days">
                                            {result.days.slice(0, 5).map((d) => (
                                                <div key={d.date_local} className="rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                                                    <div className="font-semibold" style={{ color: 'var(--t-text)' }}>{dayLabel(d.date_local)}</div>
                                                    <div style={{ color: 'var(--t-muted)' }}>{d.times.slice(0, 3).map(fmt).join(' · ')}{d.times.length > 3 ? ' …' : ''}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                                        <Button onClick={() => site.cta.onApply()} disabled={site.cta.disabled} className="csite-btn-accent csite-focus h-11 px-6" data-testid="site-travel-check-apply">
                                            {site.cta.text} <ArrowRight className="ml-1 h-4 w-4" />
                                        </Button>
                                        <Button onClick={() => setRequestOpen(true)} className="csite-btn-outline csite-focus h-11 px-5" data-testid="site-travel-check-request-alt"> {t42("None of these work? Send a request")} </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="font-semibold" style={{ color: 'var(--t-text)' }} data-testid="site-travel-check-no">
                                        {result.in_radius
                                            ? t42("Nothing open on our route near you right now")
                                            : `You're about ${result.distance_from_base_miles} miles from our home base — a bit outside our usual area`}
                                    </div>
                                    <p className="mt-0.5 text-sm" style={{ color: 'var(--t-muted)' }}>
                                        {result.in_radius
                                            ? t42("Routes change every week. Send a quick request with your address and preferred times and we’ll try to fit you in.")
                                            : t42("We sometimes make exceptions when a few families are close together. Send a request and we’ll let you know.")}
                                    </p>
                                    <Button onClick={() => setRequestOpen(true)} className="csite-btn-primary csite-focus mt-4 h-11 px-6" data-testid="site-travel-check-request">
                                        <Route className="mr-1.5 h-4 w-4" /> {t42("Request lessons at my home")} </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </Container>
            <TravelRequestDialog
                open={requestOpen}
                onOpenChange={setRequestOpen}
                slug={slug}
                mode="public"
                coachName={coach?.display_name || 'the coach'}
                address={address}
                accentColor={site.theme?.vars?.['--t-primary'] || '#00d4ff'}
            />
        </SectionShell>
    );
}

export function RenderSection({ id, site, page }) {
    const custom = (site.lp?.custom_sections || []).find((cs) => cs?.id === id);
    if (custom) {
        return (
            <Editable id={id} site={site}>
                <CustomSectionRenderer
                    section={custom}
                    coach={site.coach}
                    accent={site.theme.vars['--t-primary']}
                    accentText={site.theme.vars['--t-on-primary']}
                    headingClass="csite-display font-bold leading-tight"
                    onApply={() => site.cta.onApply()}
                />
            </Editable>
        );
    }
    const map = {
        hero: <HeroSection site={site} />,
        trust_strip: <TrustStrip site={site} />,
        highlights: <HighlightsSection site={site} />,
        programs_teaser: <ProgramsTeaser site={site} />,
        testimonials: <TestimonialsSection site={site} />,
        locations_teaser: <LocationsTeaser site={site} />,
        travel_check: <TravelCheckSection site={site} />,
        faq: <FAQSection site={site} />,
        cta_band: <CTABand site={site} />,
        about_hero: <PageHero site={site} page="about" />,
        story: <StorySection site={site} />,
        credentials: <CredentialsSection site={site} />,
        gallery: <GallerySection site={site} />,
        videos: <VideosSection site={site} />,
        programs_hero: <PageHero site={site} page="programs" />,
        packages: <PackagesSection site={site} />,
        team_hero: <PageHero site={site} page="team" />,
        team_grid: <TeamGridSection site={site} />,
        locations_hero: <PageHero site={site} page="locations" />,
        locations_grid: <LocationsGrid site={site} />,
        contact_hero: <PageHero site={site} page="contact" />,
        contact_card: <ContactCardSection site={site} />,
    };
    const node = map[id];
    if (!node) return null;
    return <Editable id={id} site={site}>{node}</Editable>;
}
